import { useEffect, useMemo, useState } from 'react'

import { useSearchParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { getErrorMessage, isSuccess } from 'redux/toolkit/api'
import { bccLogin } from 'redux/features/auth/authSlice'
import { initMonitoringServices, trackMixpanelEvent, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import { gotoAdminEntryPathFromWizard, gotoBccLogin } from 'lib/routesConfig'
import appConfig from 'config/appConfig'
import useAppTypeEntryPaths from 'components/libs/routes/useAppTypeEntryPaths'

const whitelistedHosts = [
  'https://gt3.ess.qa.cudaops.com',
  'https://staging1.ess.barracudanetworks.com',
  'https://bt2.ess.qa.cudaops.com',
  'https://au.ess.barracudanetworks.com',
  'https://ca.ess.barracudanetworks.com',
  'https://de.ess.barracudanetworks.com',
  'https://in.ess.barracudanetworks.com',
  'https://uk.ess.barracudanetworks.com',
  'https://us.ess.barracudanetworks.com'
]

export type bccLoginFailedMessage = number | string

export const useBccLoginLogic = (): [boolean, bccLoginFailedMessage?] => {
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const { userRegion, isBccLoginSuccess, bccLoginFailedMessage, user, accessToken } = useAppSelector(_store => ({
    userRegion: _store.auth.accessTokenObject?.region,
    isBccLoginSuccess: isSuccess(_store.auth.loginApiStatus),
    bccLoginFailedMessage: getErrorMessage(_store.auth.loginApiStatus),
    user: _store.auth.accessTokenObject,
    accessToken: _store.auth.accessToken
  }))
  const { appTypeEntryPath } = useAppTypeEntryPaths()
  const [error, setError] = useState(false)

  // init
  useEffect(() => {
    const migrateToken = searchParams.get(appConfig.QUERY_PARAMS.MIGRATE_TOKEN)
    const errorQueryParam = searchParams.get('error')
    const navToken = searchParams.get('nav')
    const returnUrl = searchParams.get('return_url')

    if (returnUrl) {
      try {
        const urlObject = new URL(returnUrl)
        if (whitelistedHosts.some(hostname => hostname === urlObject.origin)) {
          const encodedReturnUrl = encodeURIComponent(returnUrl)
          window.location.href = `${process.env.REACT_APP_GATEWAY_URL}/internal/bcc-login?return_url=${encodedReturnUrl}`
        }
      } catch {
        // eslint-disable-next-line no-console
        console.log('Invalid return_url')
      }
    } else if (!errorQueryParam) {
      dispatch(
        bccLogin({
          migrateToken: migrateToken || undefined,
          navToken: navToken || undefined
        })
      )
    } else {
      setError(true)
    }
    // eslint-disable-next-line
  }, [])

  // successFully logged in
  useEffect(() => {
    if (isBccLoginSuccess) {
      if (
        appConfig.ENVIRONMENT.IS_PRODUCTION &&
        userRegion &&
        !window.location.hostname.startsWith(userRegion?.toLowerCase())
      ) {
        const newRegionUrl = `${userRegion.toLowerCase()}.${window.location.hostname.substring(
          window.location.hostname.indexOf('.') + 1
        )}`
        window.location.href = `https://${newRegionUrl}${window.location.pathname}`
      } else {
        appTypeEntryPath.goto()
      }
    }
  }, [userRegion, isBccLoginSuccess, appTypeEntryPath])

  useEffect(() => {
    if (isBccLoginSuccess && user && accessToken) {
      initMonitoringServices(user, accessToken)
      trackMixpanelEvent(TRACKING_EVENTS.WEBUI.LOGIN_SUCCESS)
    }
  }, [user, accessToken, isBccLoginSuccess])

  // failed to login
  useEffect(() => {
    if (!!bccLoginFailedMessage && appConfig.APP.IS_WIZARD) gotoAdminEntryPathFromWizard()
    if (Number(bccLoginFailedMessage) === 401) gotoBccLogin()
  }, [bccLoginFailedMessage])

  return useMemo(() => [error, bccLoginFailedMessage], [error, bccLoginFailedMessage])
}
